import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MixDesignService } from 'src/app/shared/services/mix-design.service';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { geoXplore } from 'src/environments/environment';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { StrengthChartComponent } from '../models/strength-chart/strength-chart.component';
import { strengthVsWaterCementRatio } from '../data/strength-vs-watercement-ratio';

@Component({
    selector: 'app-geotherm-mix-design',
    templateUrl: './geotherm-mix-design.component.html',
    styleUrls: ['./geotherm-mix-design.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeothermMixDesignComponent implements OnInit {
  @ViewChild('locationInput', { static: false }) locationInput!: ElementRef;
  @ViewChild(MatTable) table!: MatTable<any>;
  compressiveStrength: any;

  myForm!: FormGroup;
  displayedColumns: string[] = [
    'materialType', 
    'supplier', 
    'description', 
    'weight1',
    'weight2',
    'weight3',
    'weight4', 
    'weight5',
    'weight6',
    'weight7',
    'weight8',
    'weight',
    'dryWeight',
    'precentage',
    'gravity',
    'volume',
    'actions'
  ];
  materialTypes: string[] = ['Course Aggregate', 'Fine Aggregate', 'Cementitious', 'Water', 'Add Mixer'];
  materialOptions: string[] = ['Concrete', 'FTB', 'Grout or Slurry', 'Other'];
  dataSource: MatTableDataSource<FormGroup> = new MatTableDataSource<FormGroup>([] as FormGroup[]);

  countries: any[] = [];
  states: any[] = [];
  cities: any[] = [];
  locationSuggestions: any[] = [];

  isEditMode = false;
  isCopyMode = false;
  mixDesignId: string | null = null;
  currentDate = new Date(Date.now());

  totalWeight = 0;
  totalWeightLbYd3 = 0;
  totalWater = 0;
  totalWaterLbYd3 = 0;
  totalDryWeight = 0;
  totalDryWeightLbYd3 = 0;
  totalCementitious = 0;
  waterCementRatio = 0;
  moistureContent = 0;
  dryDensity = 0;
  slurryDensity = 0;
  totalWaterGallons = 0;
  mixReferenceId: string = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private mixDesignService: MixDesignService,
    private staticDataService: LookupService,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.fetchCountries();
    this.initForm();

    this.materialFormArray.valueChanges.subscribe(() => {
      this.calculateTotals();
    });
    this.myForm.get('slurryWeight')?.valueChanges.subscribe(() => {
      this.calculateTotals();
    });
    this.myForm.get('moldVolume')?.valueChanges.subscribe(() => {
      this.calculateTotals();
    });
    this.calculateTotals();

    this.route.url.subscribe(segments => {
      const mode = segments[1]?.path;
      this.isCopyMode = mode === 'copy';
      this.isEditMode = mode === 'edit';
    });

    this.route.paramMap.subscribe(params => {
      this.mixDesignId = params.get('id');

      if ((this.isEditMode || this.isCopyMode) && this.mixDesignId) {
        this.loadMixDesign(this.mixDesignId);
      } else {
        this.seedInitialRows();
      }
    });
  }

  calculateTotals() {
    const rows = this.materialFormArray.controls;

    // slurry density calculation
    const slurryWeight = parseFloat(this.myForm.get('slurryWeight')?.value) || 0;
    const moldVolume = parseFloat(this.myForm.get('moldVolume')?.value) || 0;

    this.slurryDensity = moldVolume > 0 ? +((slurryWeight / moldVolume) * 62.4).toFixed(2) : 0;

    // Step 1: Calculate individual total (weight1..weight8) for each row
    rows.forEach(group => {
      let total = 0;
      for (let i = 1; i <= 8; i++) {
        const value = +group.get(`weight${i}`)?.value || 0;
        total += value;
      }
      group.get('weight')?.setValue(total, { emitEvent: false });
    });

    // Step 2: Calculate total weight **excluding Add Mixer**
    const calcRows = rows.filter(g => g.get('materialType')?.value !== 'Add Mixer');

    this.totalWeight = calcRows.reduce((sum, group) => sum + (+group.get('weight')?.value || 0), 0);

    // Step 3: Calculate percentage and dry weight
    calcRows.forEach(group => {
      const weight = +group.get('weight')?.value || 0;
      const percentage = this.totalWeight ? +((weight / this.totalWeight) * 100).toFixed(2) : 0;
      group.get('precentage')?.setValue(percentage, { emitEvent: false });
      const weightPercentage = (percentage / 100) * +(this.slurryDensity * 27).toFixed(2);
      group.get('dryWeight')?.setValue(+weightPercentage.toFixed(2), { emitEvent: false });
    });

    this.totalWeightLbYd3 = calcRows.reduce((sum, group) => sum + (+group.get('dryWeight')?.value || 0), 0);

    // Step 4: Category-based totals **excluding Add Mixer**
    const rowsValue = this.materialFormArray.value.filter((row: any) => row.materialType !== 'Add Mixer');

    this.totalWater = rowsValue.reduce((sum: number, row: any) =>
      row.materialType?.toLowerCase().includes('water') ? sum + (+row.weight || 0) : sum, 0);

    this.totalWaterLbYd3 = rowsValue.reduce((sum: number, row: any) =>
      row.materialType?.toLowerCase().includes('water') ? sum + (+row.dryWeight || 0) : sum, 0);

    this.totalCementitious = rowsValue.reduce((sum: number, row: any) =>
      row.materialType?.toLowerCase().includes('cement') ? sum + (+row.weight || 0) : sum, 0);

    this.totalDryWeight = +(this.totalWeight - this.totalWater).toFixed(2);
    this.totalDryWeightLbYd3 = +(this.totalWeightLbYd3 - this.totalWaterLbYd3).toFixed(2);

    this.waterCementRatio = this.totalCementitious ? +(this.totalWater / this.totalCementitious).toFixed(2) : 0;
    this.moistureContent = this.totalDryWeight ? +((this.totalWater / this.totalDryWeight) * 100).toFixed(2) : 0;
    this.dryDensity = this.totalDryWeightLbYd3 ? +(this.totalDryWeightLbYd3 / 27).toFixed(2) : 0;
    this.totalWaterGallons = this.totalWaterLbYd3 ? +(this.totalWaterLbYd3 / 8.34).toFixed(2) : 0;

    this.compressiveStrength = this.waterCementRatio
      ? strengthVsWaterCementRatio[this.waterCementRatio.toFixed(2)] || 0
      : 0;
  }

  initForm() {
    this.myForm = this.fb.group({
      mixId: [''],
      materialType: ['', Validators.required],
      countryId: [''],
      stateId: [''],
      cityId: [''],
      zipcode: [''],
      companyName: [''],
      contactName: [''],
      projectName: [''],
      location: [''],
      strength: [''],
      thermalSpecification: [''],
      slump: [''],
      timeOfEfflux: [null],
      moldId1: [''],
      moldId2: [''],
      moldVolume: [null, Validators.required],
      slurryWeight: [null, Validators.required],
      wetTestData: [''],
      dryTestData: [''],
      wetTr1: [null],
      wetTr2: [null],
      dryTr1: [null],
      dryTr2: [null],
      designedByGeotherm: [true],
      date: [null],
      note:[''],
      items: this.fb.array([])
    });
  }

  get materialFormArray(): FormArray {
    return this.myForm.get('items') as FormArray;
  }

  openStrengthPopup() {
    this.dialog.open(StrengthChartComponent, {
      width: '1000px'
    });
  }

  seedInitialRows() {
    ['Course Aggregate', 'Fine Aggregate', 'Cementitious', 'Water'].forEach(type => {
      this.addMaterialRow({ 
        materialType: type, 
        supplier: '', 
        description: '', 
        weight1: null, 
        weight2: null, 
        weight3: null, 
        weight4: null,
        weight5: null,
        weight6: null,
        weight7: null,
        weight8: null,
        weight: null,
        dryWeight: null,
        precentage: null,
        gravity: null,
        volume: null,
      });
    });
  }

  addMaterialRow(defaultValue: any = null) {
    const group = this.fb.group({
      materialType: [defaultValue?.materialType || '', Validators.required],
      supplier: [defaultValue?.supplier || ''],
      description: [defaultValue?.description || '', Validators.required],
      weight1: [defaultValue?.weight ?? null],
      weight2: [defaultValue?.weight2 ?? null],
      weight3: [defaultValue?.weight3 ?? null],
      weight4: [defaultValue?.weight4 ?? null],
      weight5: [defaultValue?.weight5 ?? null],
      weight6: [defaultValue?.weight6 ?? null],
      weight7: [defaultValue?.weight7 ?? null],
      weight8: [defaultValue?.weight8 ?? null],
      weight: [defaultValue?.weight ?? null, [Validators.min(0.000000001)]],
      dryWeight: [defaultValue?.dryWeight ?? null, [Validators.min(0.000000001)]],
      precentage: [defaultValue?.precentage ?? null, [Validators.min(0.000000001)]],
      gravity: [defaultValue?.gravity ?? null],
      volume: [defaultValue?.volume ?? null, [Validators.min(0.000000001)]],
    });

    this.materialFormArray.push(group);
    this.updateDataSource();
    this.calculateTotals();
  }

  removeRow(index: number) {
    if (!this.materialFormArray || this.materialFormArray.length <= index) return;
    this.materialFormArray.removeAt(index);
    this.updateDataSource();
    this.calculateTotals();
  }

  updateDataSource() {
    const controls = this.materialFormArray?.controls || [];
    this.dataSource = new MatTableDataSource(controls as FormGroup[]);
    if (this.table) this.table.renderRows();
  }

  loadMixDesign(id: string) {
  this.mixDesignService.getMixDesignById(id).subscribe({
    next: (data: any) => {
      this.myForm.patchValue({
        mixId: data.mixId,
        materialType: data.materialType,
        companyName: data.companyName,
        contactName: data.contactName,
        projectName: data.projectName,
        location: data.location,
        zipcode: data.zipcode,
        strength: data.strength,
        thermalSpecification: data.thermalSpecification,
        slump: data.slump,
        timeOfEfflux: data.timeOfEfflux,
        countryId: data.countryId ?? null,
        stateId: data.stateId ?? null,
        cityId: data.cityId ?? null,
        moldId1: data.moldId1,
        moldId2: data.moldId2,
        wetTr1: data.wetTr1,
        wetTr2: data.wetTr2,
        dryTr1: data.dryTr1,
        dryTr2: data.dryTr2,
        moldVolume: data.moldVolume,
        slurryWeight: data.slurryWeight,
        wetTestData: data.wetTestData,
        dryTestData: data.dryTestData,
        date: data.date ? new Date(data.date) : null,
        note: data.note,
      });
      this

      if (this.isCopyMode) {
        this.myForm.patchValue({
          mixId: '',
        });
      }

      if (data.countryId) {
        this.staticDataService.fetchState(data.countryId).subscribe(states => {
          this.states = Array.isArray(states) ? states : [states];

          if (data.stateId) {
            this.myForm.patchValue({ stateId: data.stateId });

            this.staticDataService.fetchCities(data.stateId).subscribe(cities => {
              this.cities = Array.isArray(cities) ? cities : [cities];

              if (data.cityId) {
                this.myForm.patchValue({ cityId: data.cityId });
              }
            });
          }
        });
      }

      this.materialFormArray.clear();
      (data.items || []).forEach((item: any) => this.addMaterialRow(item));
      this.updateDataSource();
      this.sortMaterialRows();
    },
      error: (err: any) => console.error(err)
    });
  }

  mapLocationToForm(place: any) {
    const ctx = place.context || [];
    let countryText = ctx.find((c: any) => c.id.includes('country'))?.text || '';
    let stateText = ctx.find((c: any) => c.id.includes('region'))?.text || '';
    let cityText = ctx.find((c: any) => c.id.includes('place'))?.text || '';
    let zipcodeText = ctx.find((c: any) => c.id.includes('postcode'))?.text || '';

    if (!cityText && place.place_type?.includes('place')) cityText = place.text;
    if (!stateText && place.place_type?.includes('region')) stateText = place.text;
    if (!countryText && place.place_type?.includes('country')) countryText = place.text;
    if (!zipcodeText) {
      const zipMatch = place.place_name.match(/\b\d{5}\b/);
      if (zipMatch) zipcodeText = zipMatch[0];
    }

    const countryObj = this.countries.find(c => c.name.toLowerCase().trim() === countryText.toLowerCase().trim());
    const countryId = countryObj?.id || '';
    this.myForm.patchValue({ countryId: countryId });

    if (!countryId) {
      this.states = [];
      this.cities = [];
      this.myForm.patchValue({ stateId: '', city: '' });
      return;
    }

    this.staticDataService.fetchState(countryId).subscribe(states => {
      this.states = Array.isArray(states) ? states : [states];
      const stateObj = this.states.find(s => s.name.toLowerCase().trim() === stateText.toLowerCase().trim());
      const stateId = stateObj?.id || '';
      this.myForm.patchValue({ stateId: stateId });

      if (!stateId) {
        this.cities = [];
        this.myForm.patchValue({ cityId: '' });
        return;
      }

      this.staticDataService.fetchCities(stateId).subscribe(cities => {
        this.cities = Array.isArray(cities) ? cities : [cities];
        const cityObj = this.cities.find(c => c.name.toLowerCase().trim() === cityText.toLowerCase().trim());
        const cityId = cityObj?.id || '';
        this.myForm.patchValue({ cityId: cityId });
      });
    });

    this.myForm.patchValue({ zipcode: zipcodeText || '' });
  }

  fetchCountries() {
    this.staticDataService.fetchCountries().subscribe(res => (this.countries = res || []));
  }

  getStates(countryId: string | null) {
    if (!countryId) {
      this.states = [];
      this.cities = [];
      return;
    }

    this.staticDataService.fetchState(countryId).subscribe(res => {
      this.states = Array.isArray(res) ? res : [res];
      this.cities = [];
    });
  }

  getCities(stateId: string | null) {
    if (!stateId) {
      this.cities = [];
      return;
    }

    this.staticDataService.fetchCities(stateId).subscribe(res => {
      this.cities = Array.isArray(res) ? res : [res];
    });
  }

  isRequired(controlName: string): boolean {
    const control = this.myForm.get(controlName);
    if (!control || !control.validator) return false;
    return control.validator({} as any)?.['required'];
  }

  getLabel(control: string): string {
    return control.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  }

  getControl(controlName: string): FormControl {
    return this.myForm.get(controlName) as FormControl;
  }

  onSubmit() {
    if (this.myForm.invalid) {
      this.myForm.markAllAsTouched();
      return;
    }

    const payload = { ...this.myForm.value };
    ['countryId', 'stateId', 'cityId'].forEach(key => {
      if (payload[key] === '') {
        payload[key] = null;
      }
    });

    if (this.isEditMode && this.mixDesignId) {
      this.mixDesignService.updateMixDesign(this.mixDesignId, payload).subscribe({
        next: () =>  {
          this.snackBar.open('Mix Design updated successfully!', 'DONE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
          });
          this.router.navigate(['mix-design']);
        },
        error: (err: any) => console.error(err)
      });
    } else {
      this.createMixDesign(payload);
    }
  }

  createMixDesign(payload: any) {
    this.mixDesignService.createMixDesign(payload).subscribe({
      next: () =>  {
        this.snackBar.open('Mix Design added successfully!', 'DONE', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['default-snackbar'],
        });
        this.router.navigate(['mix-design'])
      },
      error: (err: any) => console.error(err)
    });
  }

  cancel() {
    this.router.navigate(['mix-design']);
  }

  reDirectUrl(url?: string): void {
    if (url === 'mixDesignDashboard') {
      this.router.navigate(['mix-design']);
    } else {
      this.router.navigate(['dashboard']);
    }
  }

  sortMaterialRows() {
    if (!this.materialFormArray || !this.materialFormArray.length) return;

    const orderMap: Record<string, number> = {
      'course aggregate': 1,
      'fine aggregate': 2,
      'cementitious': 3,
      'water': 4,
      'add mixer': 5
    };

    const existingRows = this.materialFormArray.controls.filter(ctrl => ctrl.get('isNew')?.value !== true);
    const newRows = this.materialFormArray.controls.filter(ctrl => ctrl.get('isNew')?.value === true);
    const sortedExisting = [...existingRows].sort((a, b) => {
      const typeA = (a.get('materialType')?.value || '').toLowerCase();
      const typeB = (b.get('materialType')?.value || '').toLowerCase();
      return (orderMap[typeA] || 999) - (orderMap[typeB] || 999);
    });

    this.materialFormArray.clear();
    sortedExisting.forEach(ctrl => this.materialFormArray.push(ctrl));
    newRows.forEach(ctrl => this.materialFormArray.push(ctrl));
  }

  getTotal(controlName: string): number {
    if (!this.materialFormArray || this.materialFormArray.length === 0) return 0;

    const total = this.materialFormArray.controls.reduce((sum, group: any) => {
      const value = parseFloat(group.get(controlName)?.value) || 0;
      return sum + value;
    }, 0);

    return +total.toFixed(2);
  }
}
